import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const AUTH_REFRESH_PATH = "/refresh";
const AUTH_LOGIN_PATH = "/login";
const AUTH_SIGNUP_PATH = "/signup";

// Handlers globales que se pueden registrar desde los contextos de React
let errorHandler = null;
let logoutHandler = null;
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (resolve, reject) => {
  refreshSubscribers.push({ resolve, reject });
};

const notifyRefreshSubscribers = (error, newToken) => {
  refreshSubscribers.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
      return;
    }
    resolve(newToken);
  });
  refreshSubscribers = [];
};

const getStoredRefreshToken = () =>
  localStorage.getItem("refreshToken") || sessionStorage.getItem("refreshToken");

const persistSessionTokens = ({ authToken, refreshToken }) => {
  if (authToken) {
    localStorage.setItem("authToken", authToken);
  }

  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
  }
};

const getRefreshClient = () =>
  axios.create({
    baseURL: `${API_BASE_URL}/auth`,
  });

const extractTokenPayload = (data = {}) => ({
  authToken: data?.authToken || data?.accessToken || data?.token,
  refreshToken: data?.refreshToken || data?.newRefreshToken,
});

const refreshAccessToken = async () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }

  const refreshClient = getRefreshClient();
  const response = await refreshClient.post(
    AUTH_REFRESH_PATH,
    { refreshToken },
    {
      headers: {
        "x-refresh-token": refreshToken,
      },
    }
  );

  const tokens = extractTokenPayload(response?.data);
  if (!tokens.authToken) {
    throw new Error("Refresh response missing auth token");
  }

  persistSessionTokens(tokens);
  return tokens.authToken;
};

/**
 * Registra el manejador de errores global (llamado desde ToastContext)
 */
export const setErrorHandler = (handler) => {
  errorHandler = handler;
};

/**
 * Registra el manejador de logout (llamado desde AuthContext)
 */
export const setLogoutHandler = (handler) => {
  logoutHandler = handler;
};

/**
 * Mensajes de error amigables según el código HTTP
 */
const getErrorMessage = (error) => {
  if (!error.response) {
    return "Error de conexión. Verifica tu conexión a internet.";
  }

  const { status, data } = error.response;

  // Si el backend envía un mensaje de error, usarlo
  if (data?.message) {
    return data.message;
  }

  switch (status) {
    case 400:
      return "Solicitud incorrecta. Verifica los datos enviados.";
    case 401:
      return "Sesión expirada. Por favor, inicia sesión nuevamente.";
    case 403:
      return "No tienes permisos para realizar esta acción.";
    case 404:
      return "Recurso no encontrado.";
    case 409:
      return "Conflicto. El recurso ya existe.";
    case 422:
      return "Datos inválidos. Revisa el formulario.";
    case 500:
      return "Error interno del servidor. Inténtalo más tarde.";
    case 502:
    case 503:
    case 504:
      return "Servidor no disponible. Inténtalo más tarde.";
    default:
      return `Error inesperado (${status}). Inténtalo de nuevo.`;
  }
};

/**
 * Configura el interceptor de respuesta para una instancia de axios
 */
const setupResponseInterceptor = (api) => {
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error.response?.status;
      const originalRequest = error.config || {};
      const requestUrl = originalRequest.url || "";
      const isAuthEndpoint =
        requestUrl.includes(AUTH_REFRESH_PATH) ||
        requestUrl.includes(AUTH_LOGIN_PATH) ||
        requestUrl.includes(AUTH_SIGNUP_PATH);

      if (
        status === 401 &&
        !originalRequest._retry &&
        !isAuthEndpoint &&
        getStoredRefreshToken()
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh(resolve, reject);
          })
            .then((newToken) => {
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `Bearer ${newToken}`,
              };
              return api.request(originalRequest);
            })
            .catch((refreshError) => Promise.reject(refreshError));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          notifyRefreshSubscribers(null, newToken);
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${newToken}`,
          };
          return api.request(originalRequest);
        } catch (refreshError) {
          notifyRefreshSubscribers(refreshError, null);

          if (logoutHandler) {
            logoutHandler();
          }

          if (errorHandler && !originalRequest.silent) {
            errorHandler(getErrorMessage(refreshError));
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Manejo especial para 401 (no autorizado)
      if (status === 401 && logoutHandler) {
        logoutHandler();
      }

      // Mostrar error al usuario
      if (errorHandler && !originalRequest.silent) {
        const message = getErrorMessage(error);
        errorHandler(message);
      }

      // Re-lanzar el error para que los componentes puedan manejarlo también
      return Promise.reject(error);
    }
  );
};

/**
 * Crea una instancia de axios configurada con interceptores de autenticación.
 * @param {string} endpoint - El endpoint base para este servicio (ej: '/bills', '/auth')
 * @returns {import('axios').AxiosInstance} Instancia configurada de axios
 */
export const createApi = (endpoint) => {
  const api = axios.create({
    baseURL: `${API_BASE_URL}${endpoint}`,
  });

  api.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem("authToken");

    if (storedToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${storedToken}`,
      };
    }

    return config;
  });

  setupResponseInterceptor(api);

  return api;
};

/**
 * Crea una instancia de axios SIN interceptor de autenticación.
 * Útil para endpoints públicos como upload.
 * @param {string} endpoint - El endpoint base para este servicio
 * @returns {import('axios').AxiosInstance} Instancia de axios sin auth
 */
export const createPublicApi = (endpoint) => {
  const api = axios.create({
    baseURL: `${API_BASE_URL}${endpoint}`,
  });

  setupResponseInterceptor(api);

  return api;
};

export default createApi;
